import React from "react";
import { Link } from "react-router-dom";
import { Film, ArrowRight } from "lucide-react";

/**
 * Reusable Sample-Ads CTA — replaces the embedded gallery on hub/landing
 * pages with a single navigational pointer to /sample-ads.
 *
 * Mirrors the section the user approved on the Wineries & Breweries page.
 *
 * Props:
 *  - label: "Wineries & Breweries", "HVAC", "Tourism", etc.
 *  - headline: optional override; defaults to a Tourism-friendly line
 *  - description: optional override; defaults to a Tourism-friendly line
 *  - audience: "tourism" | "home-services" | "general" — only used to seed
 *    sensible default copy when headline/description aren't passed.
 *  - testIdPrefix: prefix for data-testids on the section + CTA
 */
const SampleAdsCTASection = ({
  label,
  headline,
  description,
  audience = "general",
  testIdPrefix = "sample-ads",
}) => {
  const defaults = {
    "home-services": {
      headline: (
        <>
          Real {label} spots we&apos;ve{" "}
          <span className="text-[var(--af-lime)]">produced &amp; placed.</span>
        </>
      ),
      description:
        "TV commercials, social media spots, vertical Reels, and interactive mobile display campaigns, built for real " +
        label.toLowerCase() +
        " businesses in our market.",
      cardHeading: `See every ${label} ad we've aired, filterable by format.`,
    },
    tourism: {
      headline: (
        <>
          Real {label} campaigns we&apos;ve{" "}
          <span className="text-[var(--af-lime)]">produced &amp; placed.</span>
        </>
      ),
      description:
        "Mobile display, TV, social, and Reels, built for destination marketing and proven against real visitor metrics.",
      cardHeading: `See every ${label} ad we've aired, filterable by format.`,
    },
    general: {
      headline: (
        <>
          Real campaigns we&apos;ve{" "}
          <span className="text-[var(--af-lime)]">produced &amp; placed.</span>
        </>
      ),
      description:
        "TV commercials, vertical Reels, paid-social spots, and interactive mobile display campaigns across home services and tourism brands.",
      cardHeading: "See every sample ad we've aired, filterable by format.",
    },
  };

  const fallback = defaults[audience] || defaults.general;

  // When called from a hub page, pass a `?vertical=` hint so the gallery
  // pre-selects the relevant group of industries on arrival.
  const targetUrl =
    audience === "home-services"
      ? "/sample-ads?vertical=home-services"
      : audience === "tourism"
      ? "/sample-ads?vertical=tourism"
      : "/sample-ads";

  return (
    <section
      className="px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <Film size={18} className="text-[var(--af-lime)]" />
            <div className="eyebrow">Sample Ads · {label}</div>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">
            {headline || fallback.headline}
          </h2>
          <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
            {description || fallback.description}
          </p>
        </div>
        <div
          className="surface p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center justify-between gap-5"
          data-testid={`${testIdPrefix}-cta-card`}
        >
          <div>
            <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
              Open the gallery
            </div>
            <div className="font-display text-xl sm:text-2xl leading-tight">
              {fallback.cardHeading}
            </div>
          </div>
          <Link
            to={targetUrl}
            className="btn-lime whitespace-nowrap"
            data-testid={`${testIdPrefix}-see-all`}
          >
            View sample ads <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SampleAdsCTASection;
